import './index.css';
import AppRoutes from './utils/AppRoutes';
import { BrowserRouter } from 'react-router-dom';
import AuthLoadingOverlay from './components/auth/AuthLoadingOverlay';
import { useAuthLoadingStates } from './hooks/useAuthLoadingStates';
import { useAuth0 } from './components/auth/DevAuth0Provider';
import { useEffect } from 'react';
import { SettingsProvider } from './contexts/SettingsContext';
import MaintenanceBanner from './components/common/MaintenanceBanner';
import { checkAndRestoreAuth, showInitialLoadingIfNeeded, removeLoadingOverlay } from './utils/impersonationUtils';

function App() {
  const { isAuthLoading, loadingMessage } = useAuthLoadingStates();
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  // Check for and restore authentication after impersonation navigation
  useEffect(() => {
    // Show loading overlay if needed (before any auth checks)
    showInitialLoadingIfNeeded();
    
    // This will restore auth data if we're coming from an impersonation navigation
    const authRestored = checkAndRestoreAuth();
    if (authRestored) {
      console.log('🔄 App: Authentication restored after impersonation navigation');
    }
  }, []);

  // Auto-redirect to Auth0 login for unauthenticated users
  useEffect(() => {
    // Check if we're in the middle of an impersonation navigation
    const isImpersonationNavigation = sessionStorage.getItem('impersonation_navigation') === 'true';
    
    // Only redirect to login if not in an impersonation navigation
    if (!isLoading && !isAuthenticated && !isImpersonationNavigation) {
      console.log('Auto-redirecting to Auth0 login...');
      // Use the proper loginWithRedirect method to ensure state/nonce are set correctly
      loginWithRedirect({
        appState: { returnTo: "/app" }
      });
    }
  }, [isAuthenticated, isLoading, loginWithRedirect]);
  
  // Remove loading overlay when app is fully loaded
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // Small delay to ensure app is rendered before removing overlay
      setTimeout(() => {
        removeLoadingOverlay();
      }, 300);
    }
  }, [isLoading, isAuthenticated]);

  // Show loading while redirecting
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Check if we're in the middle of an impersonation navigation
  const isImpersonationNavigation = sessionStorage.getItem('impersonation_navigation') === 'true';
  
  // Show redirecting message for unauthenticated users, but not during impersonation
  if (!isAuthenticated && !isImpersonationNavigation) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <SettingsProvider>
      <BrowserRouter>
        <MaintenanceBanner />
        <AppRoutes />

        {/* Global Authentication Loading Overlay */}
        <AuthLoadingOverlay
          isVisible={isAuthLoading}
          message={loadingMessage}
        />
      </BrowserRouter>
    </SettingsProvider>
  );
}

export default App;
